import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Tabs, Tab,
  InputAdornment, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Visibility, Block, History, Search } from '@mui/icons-material';
import axios from 'axios';

function Advertisers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', company: '' });

  useEffect(() => { fetchAdvertisers(); }, []);

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/admin/advertisers');
      setAdvertisers(response.data.data);
    } catch (error) {
      console.error('Error fetching advertisers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAdvertisers = advertisers.filter(advertiser => {
    const matchesFilter = filter === 'active' ? advertiser.status === 'active' : advertiser.status === 'inactive';
    const matchesSearch = searchTerm === '' || 
      advertiser.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (advertiser.email && advertiser.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (advertiser.company && advertiser.company.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const handleOpenDialog = () => {
    setFormData({ name: '', email: '', company: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', email: '', company: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
    if (!formData.name) {
      alert('Please fill in Name');
      return;
    }
    try {
      await axios.post('/admin/advertisers', {
        name: formData.name, email: formData.email, company: formData.company, status: 'active',
      });
      setOpenDialog(false);
      setFormData({ name: '', email: '', company: '' });
      await fetchAdvertisers();
    } catch (error) {
      console.error('Error creating advertiser:', error);
      alert(`Failed to create advertiser: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeactivate = async (advertiser) => {
    if (!window.confirm(`Are you sure you want to deactivate "${advertiser.name}"?`)) return;
    try {
      await axios.put(`/admin/advertisers/${advertiser.advertiser_id}`, { ...advertiser, status: 'inactive' });
      await fetchAdvertisers();
    } catch (error) {
      console.error('Error deactivating advertiser:', error);
      alert(`Failed to deactivate advertiser: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReactivate = async (advertiser) => {
    if (!window.confirm(`Are you sure you want to reactivate "${advertiser.name}"?`)) return;
    try {
      await axios.put(`/admin/advertisers/${advertiser.advertiser_id}`, { ...advertiser, status: 'active' });
      await fetchAdvertisers();
    } catch (error) {
      console.error('Error reactivating advertiser:', error);
      alert(`Failed to reactivate advertiser: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleViewAdvertiser = (advertiserId) => {
    navigate(`/advertisers/${advertiserId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Advertisers
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog} size={isMobile ? 'small' : 'medium'}>
          Add Advertiser
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ minHeight: { xs: 36, sm: 48 } }}>
              <Tab label="Active" value="active" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
              <Tab label="Inactive" value="inactive" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
            </Tabs>
            <TextField
              size="small"
              placeholder="Search advertisers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {!isMobile && <TableCell>ID</TableCell>}
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdvertisers.length > 0 ? (
                  filteredAdvertisers.map((advertiser) => (
                    <TableRow key={advertiser.advertiser_id} hover>
                      {!isMobile && <TableCell>{advertiser.advertiser_id}</TableCell>}
                      <TableCell>
                         <Link to={`/advertisers/${advertiser.advertiser_id}`} style={{ textDecoration: 'none' }}>
                          <Typography
                            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' }, '&:hover': { textDecoration: 'underline' } }}
                          >
                            {advertiser.name}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={advertiser.status === 'active' ? 'Active' : 'Inactive'} 
                          color={advertiser.status === 'active' ? 'success' : 'error'} 
                          size="small"
                          sx={{ color: 'white' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleViewAdvertiser(advertiser.advertiser_id)} title="View Details">
                          <Visibility fontSize="small" />
                        </IconButton>
                        {advertiser.status === 'active' && (
                          <IconButton size="small" color="error" onClick={() => handleDeactivate(advertiser)} title="Deactivate">
                            <Block fontSize="small" />
                          </IconButton>
                        )}
                        {advertiser.status === 'inactive' && (
                          <IconButton size="small" color="success" onClick={() => handleReactivate(advertiser)} title="Reactivate">
                            <History fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>No {filter} advertisers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Advertiser Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Advertiser</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} margin="normal" required autoFocus />
            <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} margin="normal" />
            <TextField fullWidth label="Company" name="company" value={formData.company} onChange={handleChange} margin="normal" />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} type="button">Cancel</Button>
            <Button type="submit" variant="contained">Create Advertiser</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Advertisers;
